import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Box, Typography, Button } from '@material-ui/core';

/**
 * Error Fallback Component for Feeds Module
 * Displays a user-friendly error message with retry functionality
 */
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={4}
      textAlign="center"
      role="alert"
      aria-live="assertive"
    >
      <Typography variant="h5" color="error" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        We're sorry, but there was an error loading the feeds. Please try again.
      </Typography>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={resetErrorBoundary}
        aria-label="Retry loading feeds"
      >
        Try Again
      </Button>
      {process.env.NODE_ENV === 'development' && error && (
        <Box mt={3} p={2} bgcolor="grey.100" borderRadius={1} maxWidth="100%">
          <Typography variant="caption" component="pre" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {error.toString()}
            {error.stack}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Functional Error Boundary for Feeds Module
 * Uses react-error-boundary library for modern functional component error handling
 */
const FeedsErrorBoundary = ({ children }) => {
  const handleError = (error, errorInfo) => {
    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Feeds Error Boundary caught an error:', error, errorInfo);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={() => {
        // Optional: Add any cleanup logic here
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default FeedsErrorBoundary;